import type { ReactNode } from "react";
import { EvalRunDetailSection } from "../EvalRunDetailElements";
import {
  ISAAC_STAGE_NOTE,
  ISAAC_SUCCESS_NOTE,
  ISAAC_TIME_NOTE,
  IsaacMetadataNotice,
  IsaacStageList,
  IsaacTaskLabel,
  isaacPercent,
  metadataTask,
  successfulTimeLabel,
} from "../IsaacEvalSection";
import { resolveRunExecution } from "../evalRunScenarioModel";
import { isaacStages, isaacSuccessRates } from "../isaacEvalModel";
import { imageMetadataState } from "../useEvalImageMetadata";
import { CompareTable, compareRunColumns, type CompareTableRow } from "./CompareTable";
import type { EvalCompareController } from "./useEvalCompareController";
import styles from "../IsaacEvalSection.module.css";

export function IsaacEvalComparison({
  controller,
  onOpenRun,
}: {
  controller: EvalCompareController;
  onOpenRun: (id: string) => void;
}): ReactNode {
  const items = controller.compareRuns;
  const contexts = items.map(({ run, key }) => {
    const execution = resolveRunExecution(run.summaryRow, run.detail, run.scenario);
    const state = imageMetadataState(controller.metadataStates, execution.tag);
    const task = metadataTask(state, execution.task);
    const load = controller.loads[key];
    const metrics = load?.metrics.status === "success" ? load.metrics.data.metrics_all : {};
    return { execution, state, task, load, metrics, stages: isaacStages(task, metrics) };
  });
  const first = contexts[0];
  const commonStages =
    first !== undefined &&
    first.task !== undefined &&
    contexts.every(
      (context) =>
        context.task !== undefined &&
        context.execution.task === first.execution.task &&
        JSON.stringify(context.task.progress_stages_order ?? []) ===
          JSON.stringify(first.task?.progress_stages_order ?? []),
    );
  const rows: CompareTableRow[] = [
    {
      key: "task",
      group: "실행",
      label: "TASK",
      cells: contexts.map(({ execution, task }) => ({
        value: <IsaacTaskLabel name={execution.task} task={task} />,
      })),
    },
    ...isaacSuccessRates({}).map(({ key, label }, index) => ({
      key,
      group: "조건 충족률",
      label,
      cells: contexts.map(({ load, metrics }) => {
        if (load?.metrics.status !== "success") {
          return { value: load?.metrics.status === "error" ? "조회 실패" : "불러오는 중…" };
        }
        const metric = isaacSuccessRates(
          metrics,
          load.terms.status === "success" ? load.terms.data : undefined,
        )[index];
        return {
          value: (
            <>
              <span>{metric.value === null ? "미측정" : isaacPercent(metric.value)}</span>
              {metric.counts !== null ? (
                <span className={styles.count}>
                  {" "}
                  · 충족 {metric.counts.satisfied} / 측정 {metric.counts.measured}개
                </span>
              ) : null}
            </>
          ),
        };
      }),
    })),
    {
      key: "success-time",
      group: "시간",
      label: "성공 평균 시간",
      cells: contexts.map(({ load }) => ({ value: successfulTimeLabel(load?.terms) })),
    },
  ];
  const hasStages = contexts.some(({ stages }) => stages.length > 0);
  if (commonStages && hasStages) {
    for (const name of first.task?.progress_stages_order ?? []) {
      rows.push({
        key: `stage:${name}`,
        group: "Stage 도달률",
        label: <span className={styles.wrapText}>{name}</span>,
        cells: contexts.map(({ load, stages }) => ({
          value:
            load?.metrics.status === "success"
              ? isaacPercent(stages.find((stage) => stage.name === name)?.value ?? null)
              : load?.metrics.status === "error"
                ? "조회 실패"
                : "불러오는 중…",
        })),
      });
    }
  }
  return (
    <EvalRunDetailSection title="Isaac Evaluation 비교">
      <CompareTable
        groupHeader="Isaac"
        leafHeader="항목"
        columns={compareRunColumns(items, onOpenRun, { showSublabel: true })}
        rows={rows}
      />
      <p className={styles.note}>{ISAAC_SUCCESS_NOTE}</p>
      <p className={styles.note}>{ISAAC_TIME_NOTE}</p>
      {hasStages ? <p className={styles.note}>{ISAAC_STAGE_NOTE}</p> : null}
      {!commonStages && hasStages && contexts.every(({ task }) => task !== undefined) ? (
        <p className={styles.note}>TASK 또는 단계 구성이 달라 실행별 Stage를 표시합니다.</p>
      ) : null}
      {contexts.map((context, index) => {
        const notice = (
          <IsaacMetadataNotice
            execution={context.execution}
            state={context.state}
            onRetry={controller.retryMetadata}
          />
        );
        const showStages = !commonStages && context.stages.length > 0;
        const showNotice =
          context.execution.tag === null ||
          context.state.status !== "success" ||
          context.task === undefined;
        if (!showStages && !showNotice) return null;
        return (
          <div key={items[index].key} className={styles.runStages}>
            {compareRunColumns([items[index]], onOpenRun, { showSublabel: true })[0].header}
            <div className={styles.task}>
              TASK <IsaacTaskLabel name={context.execution.task} task={context.task} />
            </div>
            {notice}
            {showStages ? <IsaacStageList stages={context.stages} /> : null}
          </div>
        );
      })}
    </EvalRunDetailSection>
  );
}
